import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { initialsOf } from "../../lib/initials.js";
import {
  Briefcase,
  Users,
  Video,
  Link2,
  Coins,
  Plus,
  Arrow,
  Send,
  Sparkles,
  FileText,
  ShieldCheck,
  Check,
  X,
} from "../../components/icons.js";
import { useEntitlements } from "./useEntitlements.js";
import { displayTitle } from "./displayTitle.js";
import { useOrgPlans } from "./planUsage.js";
import {
  getRequisitionStatus,
  getRequisitionDepartment,
  getRequisitionExperience,
  getRequisitionLocation,
} from "./requisitionFormat.js";
import { budgetLabel, reqStageBadge } from "./pipeline.js";

interface RequisitionRow {
  id: string;
  title: string;
  state: string;
  region?: string;
  job_description?: string;
  config?: {
    entryMode?: string;
    verticalKey?: string;
    department?: string;
    targetExperience?: string;
    location?: string;
    budgetMin?: number | string;
    budgetMax?: number | string;
    salaryMin?: number;
    salaryMax?: number;
    salaryCurrency?: string;
    currency?: string;
  };
  candidateCount?: number;
  appliedCount?: number;
  matchesCount?: number;
  scannedCount?: number;
  interviewCount?: number;
  industryKey?: string | null;
  roleFamilyKey?: string | null;
  specialisationKeys?: string[];
  createdAt?: string;
  lastActivityAt?: string;
}

interface RequisitionsResponse {
  requisitions: RequisitionRow[];
}

interface OrgStats {
  activeRequisitions: number;
  resumesScreenedThisMonth: number;
  candidatesInFlight: number;
  verifiedThisMonth: number;
  /** interviews scored this calendar month — matches the Interviews page (absent on an older API) */
  scoredThisMonth?: number;
  /** interviewed, scored, and no human decision logged yet (orgStats.ts) */
  awaitingDecision?: number;
  /** requisitions whose report is ready / awaiting the requester */
  shortlistsAwaiting?: number;
  activity?: {
    ts: string;
    kind: string;
    text: string;
    requisitionId?: string;
    candidacyId?: string;
  }[];
}

interface OrgStatsResponse {
  stats: OrgStats;
}

function renderActivityIcon(iconType: string): ReactNode {
  switch (iconType) {
    case "video":
      return <Video />;
    case "send":
      return <Send />;
    case "briefcase":
      return <Briefcase />;
    case "sparkles":
      return <Sparkles />;
    case "shield":
      return <ShieldCheck />;
    case "file":
      return <FileText />;
    case "users":
    default:
      return <Users />;
  }
}

/** "sourcing.requested" → "Sourcing requested". The API humanises the event types it knows; any
 *  it doesn't arrive raw, and a dotted machine name in a customer feed reads as a bug. */
function humanizeEventText(kind: string, text: string): string {
  if (!kind || !text.startsWith(kind)) return text;
  const words = kind.replace(/[._-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1) + text.slice(kind.length);
}

function timeAgo(iso: string): string {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

/** "Get started" checklist dismissal — per org, per browser; best-effort (storage may be blocked). */
const startedKey = (orgId: string) => `th:get-started-dismissed:${orgId}`;
function readDismissed(orgId: string | null | undefined): boolean {
  if (!orgId) return false;
  try {
    return localStorage.getItem(startedKey(orgId)) === "1";
  } catch {
    return false;
  }
}
function writeDismissed(orgId: string | null | undefined): void {
  if (!orgId) return;
  try {
    localStorage.setItem(startedKey(orgId), "1");
  } catch {
    /* best-effort */
  }
}

/** A step's state is only claimed when the page already knows it; otherwise it's a plain link. */
type StepState = "done" | "todo" | "unknown";
interface StartStep {
  key: string;
  to: string;
  label: string;
  sub: string;
  state: StepState;
}

function GetStartedCard({ steps, onDismiss }: { steps: StartStep[]; onDismiss: () => void }): ReactNode {
  const known = steps.filter((s) => s.state !== "unknown");
  const done = known.filter((s) => s.state === "done").length;
  return (
    <section className="card t1d-card t1d-start" aria-label="Get started">
      <div className="t1d-card-h">
        <div>
          <h2>Get started</h2>
          <p>A few steps to your first ranked shortlist{known.length > 0 ? ` · ${done} of ${known.length} done` : ""}</p>
        </div>
        <button
          type="button"
          className="btn ghost sm t1d-start-x"
          aria-label="Dismiss the get started checklist"
          data-tip="Hide this checklist"
          onClick={onDismiss}
        >
          <X />
        </button>
      </div>
      <ol className="t1d-start-list">
        {steps.map((s) => (
          <li key={s.key} className={`is-${s.state}`}>
            {s.state === "done" ? (
              <span className="t1d-start-mark" role="img" aria-label="Done">
                <Check />
              </span>
            ) : (
              <span className="t1d-start-mark" aria-hidden="true" />
            )}
            <Link to={s.to} className="t1d-start-link">
              <span className="t">{s.label}</span>
              <span className="d">{s.sub}</span>
            </Link>
            <Arrow className="t1d-start-go" aria-hidden="true" />
          </li>
        ))}
      </ol>
    </section>
  );
}

export function Dashboard(): ReactNode {
  const { me } = useAuth();
  // Live Requisitions
  const reqsQuery = useQuery({
    queryKey: ["requisitions"],
    queryFn: () => api.get<RequisitionsResponse>("/requisitions"),
  });

  // Plan usage (0094) — AI interviews left in the organisation's pool
  const plansQuery = useOrgPlans();

  // Live Org Stats
  const statsQuery = useQuery({
    queryKey: ["org-stats", me?.orgId],
    queryFn: () => api.get<OrgStatsResponse>(`/orgs/${me!.orgId}/stats`),
    enabled: !!me?.orgId,
  });

  // Connector count — same endpoint the Connections page reads.
  const connectorsQuery = useQuery({
    queryKey: ["connectors", me?.orgId ?? ""],
    queryFn: () =>
      api.get<{ connectors: { status: string; enabled: boolean }[] }>(`/orgs/${me?.orgId}/connectors`),
    enabled: Boolean(me?.orgId),
    staleTime: 60_000,
    retry: false,
  });
  const activeConnectors = (connectorsQuery.data?.connectors ?? []).filter(
    (c) => c.enabled && c.status === "active",
  ).length;

  const rawRequisitions = reqsQuery.data?.requisitions ?? [];
  const isOpenReq = (r: RequisitionRow) => r.state !== "closed" && r.state !== "archived";

  // Open roles first, ACTIVE ones ahead of drafts, most recently touched first; if every role is
  // closed, show the closed ones rather than nothing. Never invented requisitions for an empty org.
  const liveRequisitions = useMemo(() => {
    const open = rawRequisitions.filter(isOpenReq);
    const list = open.length > 0 ? open : rawRequisitions;
    const t = (r: RequisitionRow): number => Date.parse(r.lastActivityAt ?? r.createdAt ?? "") || 0;
    const act = (r: RequisitionRow): number => (getRequisitionStatus(r.state) === "Active" ? 1 : 0);
    return [...list].sort((a, b) => act(b) - act(a) || t(b) - t(a));
  }, [rawRequisitions]);

  const hasRequisitions = rawRequisitions.length > 0;
  const openCount = rawRequisitions.filter(isOpenReq).length;
  // Honest split (product review: "13 Active Positions" counted drafts). Active = running roles.
  const statusCounts = useMemo(() => {
    const n = { Active: 0, Draft: 0, Hold: 0, Closed: 0 };
    for (const r of rawRequisitions) n[getRequisitionStatus(r.state)]++;
    return n;
  }, [rawRequisitions]);

  // The real remaining count, or nothing until it loads — never a guessed placeholder.
  const interviewUsage = plansQuery.data?.usage.interview;
  const interviewsLeft = interviewUsage?.remaining;
  const interviewsLow =
    interviewUsage != null && interviewUsage.total > 0 && interviewUsage.remaining / interviewUsage.total < 0.1;

  const sum = (pick: (r: RequisitionRow) => number | undefined): number =>
    rawRequisitions.reduce((acc, r) => acc + (pick(r) ?? 0), 0);
  const totalCandidatesAcrossReqs = useMemo(() => sum((r) => r.candidateCount), [rawRequisitions]);
  // Funnel totals for the pipeline card — straight sums of the per-requirement counts the list
  // endpoint returns. `?? 0`, never a share of another count (no manufactured match rates).
  const funnel = useMemo(() => {
    const open = rawRequisitions.filter(isOpenReq);
    const s = (pick: (r: RequisitionRow) => number | undefined): number =>
      open.reduce((acc, r) => acc + (pick(r) ?? 0), 0);
    return {
      applied: s((r) => r.appliedCount ?? r.candidateCount),
      scanned: s((r) => r.scannedCount),
      matched: s((r) => r.matchesCount),
      interviewed: s((r) => r.interviewCount),
    };
  }, [rawRequisitions]);

  const stats = statsQuery.data?.stats;
  const resumesScored = stats?.resumesScreenedThisMonth ?? 0;
  const interviewsThisMonth = stats?.scoredThisMonth ?? stats?.verifiedThisMonth ?? 0;
  const awaitingDecision = stats?.awaitingDecision ?? 0;
  const reportsReady = stats?.shortlistsAwaiting ?? 0;

  // Matched but not yet interviewed — both sides from the same per-requirement counts. Floors at 0
  // (a directly-added candidate can be interviewed without ever having been a match).
  const matchedReadyCount = Math.max(0, funnel.matched - funnel.interviewed);

  const activityItems = useMemo(() => {
    const act = stats?.activity ?? [];
    // No invented timeline. An org with no activity has an empty feed, and the panel says so.
    return act.slice(0, 6).map((a, idx) => {
      let typeLabel = "Update";
      let iconType = "users";
      let tone = "neutral";
      let targetLink = "/app/requisitions";

      if (a.kind.includes("candidate") || a.kind.includes("applicant") || a.kind.includes("screen")) {
        typeLabel = "Candidates";
        iconType = "users";
        tone = "brand";
        targetLink = a.requisitionId ? `/app/candidates?reqId=${a.requisitionId}` : "/app/candidates";
      } else if (a.kind.includes("interview") || a.kind.includes("report")) {
        typeLabel = "Interview";
        iconType = "video";
        tone = "info";
        targetLink = a.candidacyId ? `/app/review/${a.candidacyId}` : "/app/interviews";
      } else if (a.kind.includes("invite")) {
        typeLabel = "Invites";
        iconType = "send";
        tone = "info";
        targetLink = a.requisitionId ? `/app/candidates?reqId=${a.requisitionId}` : "/app/candidates";
      } else if (a.kind.includes("decision") || a.kind.includes("shortlist")) {
        typeLabel = "Decision";
        iconType = "shield";
        tone = "brand";
        targetLink = a.candidacyId ? `/app/review/${a.candidacyId}` : "/app/candidates";
      } else if (a.kind.includes("requisition") || a.kind.includes("sourcing") || a.kind.includes("collect")) {
        typeLabel = "Requirement";
        iconType = "briefcase";
        tone = "neutral";
        targetLink = a.requisitionId ? `/app/requisitions/${a.requisitionId}` : "/app/requisitions";
      } else if (a.requisitionId) {
        targetLink = `/app/requisitions/${a.requisitionId}`;
      }

      return {
        id: `act-live-${idx}`,
        typeLabel,
        iconType,
        tone,
        title: humanizeEventText(a.kind, a.text),
        targetLink,
        timestamp: timeAgo(a.ts),
      };
    });
  }, [stats?.activity]);

  // "Needs you" — built only from numbers this page already loads. Each row is a real count > 0.
  const todos: { key: string; to: string; icon: ReactNode; title: string; sub: string; cta?: string }[] = [];
  if (awaitingDecision > 0) {
    todos.push({
      key: "decide",
      to: "/app/interviews",
      icon: <ShieldCheck />,
      title: `${awaitingDecision} interview${awaitingDecision === 1 ? "" : "s"} awaiting your decision`,
      sub: "Scored by AI — a person decides who moves on",
    });
  }
  if (reportsReady > 0) {
    todos.push({
      key: "reports",
      to: "/app/requisitions",
      icon: <FileText />,
      title: `${reportsReady} requirement report${reportsReady === 1 ? "" : "s"} ready`,
      sub: "Ranked shortlist ready for your review",
    });
  }
  if (matchedReadyCount > 0) {
    todos.push({
      key: "invite",
      to: "/app/candidates",
      icon: <Send />,
      title: `${matchedReadyCount} matched — not yet interviewed`,
      sub: "Send AI interview invites",
      cta: "Send interview invites",
    });
  }
  if (hasRequisitions && activeConnectors === 0 && connectorsQuery.isSuccess) {
    todos.push({
      key: "intake",
      to: "/app/connections",
      icon: <Link2 />,
      title: "Set up candidate intake",
      sub: "No email or webhook feed is connected yet",
    });
  }

  // "Get started" checklist — new orgs (no requirements yet) and FREE-plan orgs, until dismissed.
  const { entitlements } = useEntitlements();
  const [startDismissed, setStartDismissed] = useState(() => readDismissed(me?.orgId));
  const showStart =
    reqsQuery.isSuccess && !startDismissed && !readDismissed(me?.orgId) && (!hasRequisitions || entitlements.freeTier);
  const anyInterviews = sum((r) => r.interviewCount) > 0;
  const firstReq = liveRequisitions[0];
  const startSteps: StartStep[] = [
    {
      key: "req",
      to: "/app/new",
      label: "Create your first requirement",
      sub: "Paste a job description — TalentHop builds the screening and interview plan",
      state: hasRequisitions ? "done" : "todo",
    },
    {
      key: "intake",
      to: "/app/connections",
      label: "Set up email applications",
      sub: "Forward applications by email so resumes land on the right job",
      state: connectorsQuery.isSuccess ? (activeConnectors > 0 ? "done" : "todo") : "unknown",
    },
    {
      key: "team",
      to: "/app/team",
      label: "Invite your team",
      sub: "Bring in the colleagues who review candidates",
      state: "unknown",
    },
    {
      key: "interview",
      to: firstReq ? `/app/requisitions/${firstReq.id}` : "/app/interviews",
      label: "Send your first AI interview",
      sub: "Invite matched candidates to a voice AI interview",
      // Only a completed interview proves one was sent; zero doesn't prove none was.
      state: anyInterviews ? "done" : "unknown",
    },
  ];

  const firstName = (me?.name ?? "").trim().split(/\s+/)[0] ?? "";
  const funnelMax = Math.max(1, funnel.applied, funnel.scanned, funnel.matched, funnel.interviewed);
  const funnelRows = [
    { key: "scanned", label: "Resumes scored", n: funnel.scanned, tone: "f-1" },
    { key: "applied", label: "Candidates on the roles", n: funnel.applied, tone: "f-2" },
    { key: "matched", label: "Matched", n: funnel.matched, tone: "f-3" },
    { key: "interviewed", label: "Interviewed", n: funnel.interviewed, tone: "f-4" },
  ];

  return (
    <div className="dashboard-container t1d">
      {/* Page header (X1) */}
      <header className="t1d-head">
        <div>
          <h1>{firstName ? `${greeting()}, ${firstName}` : greeting()}</h1>
          <p>
            Here is where your hiring stands today
            {todos.length > 0 ? " — here’s what needs you." : "."}
          </p>
        </div>
        <div className="t1d-head-actions">
          <Link to="/app/requisitions" className="btn">
            <Briefcase /> All requirements
          </Link>
        </div>
      </header>

      {showStart && (
        <GetStartedCard
          steps={startSteps}
          onDismiss={() => {
            writeDismissed(me?.orgId);
            setStartDismissed(true);
          }}
        />
      )}

      <div className="t1d-grid">
        <div className="t1d-col">
          {/* KPIs — one dark card per view */}
          <section className="t1d-kpis" aria-label="Key numbers">
            <Link to="/app/requisitions" className="t1d-kpi is-dark">
              <div className="t1d-kpi-top">
                <span>Active requirements</span>
                <span className="t1d-kpi-ico"><Briefcase /></span>
              </div>
              <div className="t1d-kpi-val">{statusCounts.Active}</div>
              <div className="t1d-kpi-sub">
                {statusCounts.Draft} draft{statusCounts.Draft === 1 ? "" : "s"}
                {statusCounts.Hold > 0 ? ` · ${statusCounts.Hold} on hold` : ""}
                {statusCounts.Closed > 0 ? ` · ${statusCounts.Closed} closed` : ""}
              </div>
            </Link>

            <Link to="/app/candidates" className={`t1d-kpi${resumesScored === 0 ? " is-zero" : ""}`}>
              <div className="t1d-kpi-top">
                <span>Resumes scored</span>
                <span className="t1d-kpi-ico"><Users /></span>
              </div>
              <div className="t1d-kpi-val">{resumesScored}</div>
              <div className="t1d-kpi-sub">
                This month · {totalCandidatesAcrossReqs} candidate{totalCandidatesAcrossReqs === 1 ? "" : "s"} on all roles
              </div>
            </Link>

            <Link to="/app/interviews" className={`t1d-kpi${interviewsThisMonth === 0 ? " is-zero" : ""}`}>
              <div className="t1d-kpi-top">
                <span>AI interviews scored</span>
                <span className="t1d-kpi-ico"><Video /></span>
              </div>
              <div className="t1d-kpi-val">{interviewsThisMonth}</div>
              <div className="t1d-kpi-sub">
                This month
                {awaitingDecision > 0 ? ` · ${awaitingDecision} awaiting decision` : ""}
              </div>
            </Link>

            <Link to="/app/plans" className="t1d-kpi">
              <div className="t1d-kpi-top">
                <span>Interviews left</span>
                <span className="t1d-kpi-ico"><Coins /></span>
              </div>
              <div className="t1d-kpi-val">{interviewsLeft != null ? interviewsLeft.toLocaleString("en-IN") : "—"}</div>
              <div className={`t1d-kpi-sub${interviewsLow ? " is-warn" : ""}`}>
                {interviewUsage ? `of ${interviewUsage.total.toLocaleString("en-IN")} in your plan` : "Plans & usage"}
                {interviewsLow ? " · running low" : ""}
              </div>
            </Link>
          </section>

          {/* FIRST RUN — an org with no requirements gets a way in, not an empty grid. */}
          {!reqsQuery.isLoading && !hasRequisitions && (
            <section className="card t1d-card t1d-empty">
              <span className="t1d-empty-ico"><Briefcase /></span>
              <h2>Let's get your first role running</h2>
              <p>
                Create a requirement and TalentHop screens resumes against it, runs voice AI
                interviews, and ranks candidates. You can upload resumes yourself, or switch on
                Email receiving and have applications forwarded straight to the job.
              </p>
              <div className="t1d-empty-actions">
                <Link to="/app/new" className="btn primary">
                  <Plus /> Create your first requirement
                </Link>
                <Link to="/app/connections" className="btn">
                  Set up connections
                </Link>
              </div>
            </section>
          )}

          {/* Requirements — compact table (desktop), stacked cards (phone) */}
          {hasRequisitions && (
            <section className="card t1d-card t1d-reqs">
              <div className="t1d-card-h">
                <div>
                  <h2>Open requirements</h2>
                  <p>Counts are people who have reached each stage</p>
                </div>
                <Link to="/app/requisitions" className="btn sm">
                  View all {openCount}
                </Link>
              </div>
              <div className="t1d-tbl-wrap">
                <table className="t1d-tbl">
                  <thead>
                    <tr>
                      <th>Requirement</th>
                      <th className="num">Applied</th>
                      <th className="num t1d-hide-md">Scored</th>
                      <th className="num">Matches</th>
                      <th className="num">Interviewed</th>
                      <th className="t1d-hide-md">Budget</th>
                      <th>Stage</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {liveRequisitions.slice(0, 6).map((req) => {
                      const title = displayTitle(req.title);
                      const stage = reqStageBadge(req.state);
                      const budget = budgetLabel(req);
                      const applied = req.appliedCount ?? req.candidateCount ?? 0;
                      const scanned = req.scannedCount ?? 0;
                      const matches = req.matchesCount ?? 0;
                      const interviews = req.interviewCount ?? 0;
                      const meta = [getRequisitionDepartment(req), getRequisitionExperience(req), getRequisitionLocation(req)]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <tr key={req.id}>
                          <td className="t1d-req">
                            <div className="t1d-who">
                              <span className="t1d-av" aria-hidden="true">{initialsOf(title, "RQ").slice(0, 2)}</span>
                              <div>
                                <Link to={`/app/requisitions/${req.id}`} className="t1d-req-title" title={title}>
                                  {title}
                                </Link>
                                <small title={meta}>{meta}</small>
                              </div>
                            </div>
                          </td>
                          <td className="num" data-label="Applied">{applied}</td>
                          <td className="num t1d-hide-md" data-label="Scored">{scanned}</td>
                          <td className="num" data-label="Matches">{matches}</td>
                          <td className="num" data-label="Interviewed">{interviews}</td>
                          <td className="t1d-hide-md t1d-budget">{budget ?? <span className="t1d-mute">Not set</span>}</td>
                          <td className="t1d-stage"><span className={stage.cls}>{stage.label}</span></td>
                          <td className="t1d-act">
                            <Link
                              to={`/app/candidates?reqId=${req.id}`}
                              className="t1d-go"
                              aria-label={`View candidates for ${title}`}
                              data-tip="View candidates"
                              data-tip-pos="left"
                            >
                              <Arrow />
                              <span className="t1d-go-lbl">View candidates</span>
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <aside className="t1d-col">
          {/* Needs you */}
          <section className="card t1d-card t1d-todo">
            <div className="t1d-card-h">
              <h2>Needs you</h2>
              {todos.length > 0 && <span className="pill r">{todos.length}</span>}
            </div>
            {todos.length === 0 ? (
              <p className="t1d-mute t1d-todo-none">
                {statsQuery.isLoading ? "Checking…" : "Nothing is waiting on you right now."}
              </p>
            ) : (
              <ul>
                {todos.map((t) => (
                  <li key={t.key}>
                    <Link to={t.to} className="t1d-todo-row">
                      <span className="t1d-ico-s">{t.icon}</span>
                      <span className="t1d-todo-txt">
                        <b>{t.title}</b>
                        <small>{t.sub}</small>
                      </span>
                      {t.cta ? <span className="btn sm primary t1d-todo-cta">{t.cta}</span> : <Arrow className="t1d-chev" />}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {activeConnectors > 0 && (
              <Link to="/app/connections" className="t1d-todo-foot">
                <Link2 /> {activeConnectors} intake feed{activeConnectors === 1 ? "" : "s"} connected
              </Link>
            )}
          </section>

          {/* Pipeline — real sums of the per-requirement counts, open requirements only */}
          {hasRequisitions && (
            <section className="card t1d-card t1d-pipe">
              <div className="t1d-card-h">
                <div>
                  <h2>Pipeline totals</h2>
                  <p>Open requirements · direct adds can skip stages</p>
                </div>
              </div>
              <ul className="t1d-funnel">
                {funnelRows.map((f) => (
                  <li key={f.key}>
                    <span className="t1d-funnel-lbl">{f.label}</span>
                    <span className="t1d-funnel-n tnum">{f.n}</span>
                    <span className="t1d-funnel-bar" aria-hidden="true">
                      <i className={f.tone} style={{ width: `${(f.n / funnelMax) * 100}%` }} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Recent activity */}
          <section className="card t1d-card t1d-activity">
            <div className="t1d-card-h">
              <h2>Recent activity</h2>
              <Link to="/app/candidates" className="btn sm ghost">See candidates</Link>
            </div>
            {activityItems.length === 0 ? (
              <p className="t1d-mute">
                No activity yet. Candidate intake, interviews and decisions will appear here as they happen.
              </p>
            ) : (
              <ul className="t1d-feed">
                {activityItems.map((act) => (
                  <li key={act.id}>
                    <Link to={act.targetLink}>
                      <span className={`t1d-feed-ico tone-${act.tone}`}>{renderActivityIcon(act.iconType)}</span>
                      <span className="t1d-feed-txt">
                        <span className="t1d-feed-title">{act.title}</span>
                        <small>{act.typeLabel} · {act.timestamp}</small>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>

      {/* Ground Rules & Honest Compliance Disclaimer */}
      <div className="disclaimer t1d-honesty">
        <b>Facial AI is proof only</b> — identity, liveness, injection &amp; second-person checks; it never
        scores face, tone, affect or personality. The engine ranks — a human always decides, and rejection
        is human-gated.
      </div>
    </div>
  );
}
